/**
 * PositionQueueWidget - the preparer/reviewer/approver pill strip that
 * lives in the AppShell header. Driven by:
 *   useMe()         -> which positions to highlight
 *   useCapCounts()  -> the badge number on each pill
 *
 * Module placement: lives under framework/ (not shared/) because it
 * reads framework state (CAP queue counts). The composition root
 * (App.tsx) is the only place allowed to import framework/ AND mount
 * this widget into AppShell's `header` slot - the P0.8 module-
 * boundary rule keeps shared/ feature-free.
 *
 * Visibility model:
 *   - Anonymous (useMe loading / error 401) -> nothing rendered.
 *   - Authenticated user holding at least one position -> all three
 *     pills render. Pills the user does NOT hold are dimmed + carry an
 *     aria-label hint that the count is informational only. Admins see
 *     all three as held.
 *   - Authenticated user holding NONE -> nothing rendered (round 9 E9).
 *     Peeking at a neighbouring queue is for someone who is in the
 *     workflow; to a service owner, who is not, the strip was three
 *     working links into queues he can never act in, rendered on every
 *     screen because this sits in the masthead, three inches above the
 *     403 its own count call took. That is the repeat class of this
 *     programme -- an action offered to a seat that cannot complete it
 *     -- surviving in the one component every screen mounts.
 *   - 0-count pills still render (so the strip is stable; chips
 *     don't pop in/out as work moves).
 *
 * Each pill is a router NavLink to /framework/queue/{position}.
 * P1.3 wires the QueueView at that route; for now it 404s into the
 * existing /framework hello world. The link target is intentional -
 * stable URLs survive a missing view.
 *
 * RTL: logical Tailwind (ms-/me-) only, per the v2.1 RTL discipline.
 */
import { InfoTip } from "shared/components/Explainer";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useCapCounts } from "framework/api/hooks";
import { useAssessmentQueueCounts } from "framework/api/assessment_hooks";
import { hasPosition, useMe } from "shared/api/useMe";
import { hasModule } from "shared/api/authority";
import { ALL_POSITIONS, type Position } from "shared/api/types";

/** Where the widget renders:
 *   "dark"  -- on the navy header band / full-bleed floating card
 *              (brass-on-navy pills, the original look).
 *   "light" -- P214.5: in the Home daylight ribbon (white "qpill"
 *              capsules with a brass count circle, on paper). */
export type QueueTone = "dark" | "light";

interface PillProps {
  position: Position;
  count: number;
  held: boolean;
  tone: QueueTone;
}

function Pill({ position, count, held, tone }: PillProps) {
  const { t } = useTranslation();
  const label = t(`queue.${position}`);
  const tooltip = t("queue.countTooltip", { count });

  // Held positions get the brand-accent treatment, signalling "this is
  // yours". Not-held are muted but still linkable -- reviewers can peek
  // at the preparer queue read-only.
  let base: string;
  let pillStyle: string;
  let badgeStyle: string;
  if (tone === "light") {
    // qpill: a white rounded capsule with a brass count circle (the
    // Observatory daylight ribbon look).
    base =
      "ps-[11px] pe-[5px] py-[3px] rounded-full text-[11.5px] font-medium flex items-center gap-[7px] border";
    pillStyle = held
      ? "border-brand-brassLine bg-white text-ink-2 hover:border-brand-brass"
      : "border-paper-3 bg-white/70 text-ink-muted hover:text-ink-2";
    badgeStyle = held ? "bg-brand-brass text-white" : "bg-paper-3 text-ink-2 font-mono";
  } else {
    base = "px-2.5 py-1 rounded-md text-xs font-medium flex items-center gap-1.5";
    pillStyle = held
      ? "bg-brand-brass text-brand-navy hover:bg-brand-brassDark"
      : "bg-brand-navyDeep text-sky-nav hover:text-brand-cream";
    badgeStyle = held
      ? "bg-brand-navy text-brand-brass"
      : "bg-slate-900 text-slate-300";
  }

  return (
    <NavLink
      to={`/framework/queue/${position}`}
      title={tooltip}
      aria-label={
        held ? `${label}: ${tooltip}` : `${label} (${t("queue.notMine")}): ${tooltip}`
      }
      className={`${base} ${pillStyle}`}
    >
      <span>{label}</span>
      <span
        className={[
          "inline-flex items-center justify-center rounded-full font-semibold",
          tone === "light"
            ? "w-[18px] h-[18px] text-[10.5px]"
            : "min-w-5 h-5 text-[10px]",
          badgeStyle,
        ].join(" ")}
        aria-hidden="true"
      >
        {count}
      </span>
    </NavLink>
  );
}

export function PositionQueueWidget({ tone = "dark" }: { tone?: QueueTone } = {}) {
  const { t } = useTranslation();
  const me = useMe();
  // Round 9 E9 -- someone in the workflow at all? Admins hold every
  // position, so `hasPosition` settles both cases.
  const inWorkflow = ALL_POSITIONS.some((p) => hasPosition(me.data, p));
  /* Round 10 F5 -- and holding a position is not the same as holding
     the module. A service owner holds `preparer`, so this asked the
     framework for his queue counts on every render of every page and
     took a 403 each time: twenty-three of the twenty-nine console
     entries on his walk came from here and its sibling. */
  const inFramework = inWorkflow && hasModule(me.data, "framework");
  const counts = useCapCounts(inFramework);
  // D9 -- the chip counted CAPs only, so a reviewer read "1" above a
  // queue listing one CAP and two assessments. The chip counts what the
  // queue lists.
  //
  // E9: and it fired for a seat holding no position, which cannot read
  // it. The strip does not render for them either, so there is nothing
  // the answer would have filled in.
  const assessmentCounts = useAssessmentQueueCounts(inFramework);

  // Anonymous (no user yet) -> render nothing. The empty header is
  // honest about state - flashing a logged-in widget for an
  // unauthenticated user would be a worse first impression than a
  // momentary gap.
  // The strip is the framework's queue. Rendering it to somebody the
  // framework will refuse offers three links into a refusal.
  if (!me.data || !inFramework) {
    return null;
  }

  const labelClass =
    tone === "light"
      ? "font-mono text-[10.5px] uppercase tracking-[.18em] text-ink-muted me-1"
      : "text-xs text-sky-nav me-1";

  return (
    /* G3 -- wraps on a phone rather than pushing the page sideways. */
    <div
      className="flex flex-wrap items-center gap-2 sm:ms-4"
      aria-label={t("queue.label")}
    >
      <span className={labelClass}>
        {t("queue.label")}
        {/* F2: "MY QUEUES" with three chips was step 1 for the reviewer
            and the preparer, and neither knew that a position is what
            you may DO rather than the job you hold. The widget renders
            on the navy band and on the light Home ribbon. */}
        <InfoTip termKey="positions" tone={tone === "light" ? "light" : "dark"} />
      </span>
      {ALL_POSITIONS.map((position) => (
        <Pill
          key={position}
          position={position}
          count={
            (counts.data?.[position] ?? 0) + (assessmentCounts.data?.[position] ?? 0)
          }
          held={hasPosition(me.data, position)}
          tone={tone}
        />
      ))}
    </div>
  );
}
